/**
 * Product Grid Video Handler
 * Handles autoplay and lazy loading for videos in product cards
 */

(function() {
  'use strict';

  function showVideoFallback(video) {
    const card = video.closest('.card-product__media');
    if (!card) return;

    const fallback = card.querySelector('.card-product__video-fallback');
    const videoContainer = card.querySelector('.card-product__video');
    if (!fallback || !videoContainer) return;

    videoContainer.style.display = 'none';
    fallback.style.setProperty('display', 'block', 'important');
  }

  function shouldSkipAutoplay() {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const saveData = Boolean(navigator.connection && navigator.connection.saveData);
    return prefersReducedMotion || saveData;
  }
  
  /**
   * Initialize product grid videos
   * Uses IntersectionObserver for lazy loading and autoplay handling
   */
  function initProductGridVideos() {
    const productVideos = document.querySelectorAll('[data-product-video] video');
    if (productVideos.length === 0) return;
    
    // Check if IntersectionObserver is supported
    if (!('IntersectionObserver' in window)) {
      // Fallback: Try to play all videos immediately when motion/data preferences allow
      productVideos.forEach(video => {
        if (shouldSkipAutoplay()) {
          showVideoFallback(video);
          return;
        }

        if (video.muted) {
          video.play().catch(() => {
            showVideoFallback(video);
          });
        }
      });
      return;
    }
    
    // Create observer for lazy loading and autoplay
    const observerOptions = {
      root: null, // viewport
      rootMargin: '50px 0px', // Start loading when 50px away from viewport
      threshold: [0, 0.1] // Trigger when video enters viewport
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const video = entry.target;
        
        if (entry.isIntersecting) {
          if (shouldSkipAutoplay()) {
            showVideoFallback(video);
            observer.unobserve(video);
            return;
          }

          // Video is in viewport or approaching
          // Ensure muted for autoplay compliance
          video.muted = true;
          
          // Try to play the video
          const playPromise = video.play();
          if (playPromise !== undefined) {
            playPromise.then(() => {
              // Playback started successfully
            }).catch(error => {
              // Autoplay failed (usually browser policy)
              console.log('Product grid video autoplay failed:', error);
              showVideoFallback(video);
            });
          }
          
          // Once video starts playing, we can unobserve it
          video.addEventListener('playing', () => {
            observer.unobserve(video);
          }, { once: true });
        }
      });
    }, observerOptions);
    
    // Observe all product grid videos
    productVideos.forEach(video => {
      // Ensure muted for autoplay
      video.muted = true;
      observer.observe(video);
      
      // Handle video error - show fallback image
      video.addEventListener('error', () => {
        showVideoFallback(video);
      }, { once: true });
    });
  }
  
  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductGridVideos);
  } else {
    initProductGridVideos();
  }
  
  // Re-initialize when new content is loaded (e.g., infinite scroll, filters)
  document.addEventListener('productGridUpdated', initProductGridVideos);
  
})();
